/**
 * Thin collapse wrapper for Engagement / ECM Insights cards.
 *
 * The wrapped child IS a Card already (each xxxCard.tsx renders its own
 * <Card>). This component sits OUTSIDE that card and renders a small
 * clickable header bar that hides/shows the card body. Collapsed state
 * persists per id in localStorage.
 *
 * Default behavior: collapsed (so the panel is skimmable). Pass
 * `defaultOpen` for hero cards (e.g., trend chart).
 */

import React, { useState, useCallback, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface Props {
  id: string;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

const STORAGE_PREFIX = 'insights-card:';

export const CollapsibleCard: React.FC<Props> = ({
  id,
  title,
  subtitle,
  defaultOpen = false,
  children,
}) => {
  const storageKey = `${STORAGE_PREFIX}${id}`;
  const [open, setOpen] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(storageKey);
      if (v === '1') return true;
      if (v === '0') return false;
    } catch {
      /* SSR / disabled storage */
    }
    return defaultOpen;
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, open ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [open, storageKey]);

  const toggle = useCallback(() => setOpen((v) => !v), []);

  return (
    <div>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="w-full flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-2 text-left text-sm font-medium text-gray-800 hover:bg-gray-50 transition-colors"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-500 flex-shrink-0" />
        )}
        <span className="flex-1 min-w-0">
          {title}
          {subtitle && (
            <span className="ml-2 text-xs font-normal text-gray-500">{subtitle}</span>
          )}
        </span>
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
};
